import React, { FC, MouseEventHandler } from "react";
import styled, { css } from "styled-components";
import { Button, Text, tombac } from "tombac";
import { MeProductAccess } from "../../experimental";
import { getProductName } from "../../shared";

export interface TrialLimitSummaryProps {
  activeProduct: string;
  onContinueClick?: MouseEventHandler;
  productAccess: MeProductAccess;
  size?: "s" | "m" | "l";
}

const BrandButton = styled(Button).attrs(() => ({ variant: "danger" }))`
  text-transform: uppercase;
`;

const Content = styled.div`
  margin-left: ${tombac.space(3)};
`;

const DaysRemainingCaption = styled.span`
  height: 0;
`;

const DaysRemainingNumber = styled.strong`
  all: initial;
  ${tombac.text({ fontSize: 36, fontWeight: 500, lineHeight: 1 })}
`;

const DaysRemaining = styled.div`
  all: initial;
  align-items: center;
  box-sizing: border-box;
  box-shadow: ${tombac.unit(0, 2, 4, 0)} ${tombac.alpha("neutral", 16)};
  display: flex;
  flex: none;
  flex-direction: column;
  height: ${tombac.unit(73)};
  justify-content: center;
  position: relative;
  width: ${tombac.unit(83)};
  z-index: 1;
  ${tombac.text({ color: tombac.color("neutral", 700), fontSize: 8 })}

  &::before {
    background-color: ${tombac.color("neutral", 200)};
    bottom: 0;
    content: "";
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: -1;
  }

  &::after {
    background-image: linear-gradient(
      to top,
      ${tombac.color("neutral", 200)} 63%,
      ${tombac.color("neutral", 400)} 133%
    );
    bottom: 0;
    content: "";
    left: 0;
    height: ${tombac.unit(36)};
    position: absolute;
    right: 0;
    z-index: -1;
  }
`;

const ExpiryDate = styled.span`
  all: initial;
  display: block;
  ${tombac.text({ color: tombac.color("neutral", 500), fontSize: 10 })}

  & > strong {
    all: inherit;
    display: inline;
  }
`;

const LimitList = styled.ul`
  all: initial;
  display: block;
  margin-top: ${tombac.space(2)};
`;

const LimitItem = styled.li`
  all: initial;
  display: block;
  ${tombac.text({ color: tombac.color("neutral", 700), fontSize: 10 })}

  &::before {
    background-color: ${tombac.color("alert", 600)};
    border-radius: 50%;
    content: "";
    display: inline-block;
    height: ${tombac.unit(4)};
    margin: -0.2em ${tombac.space(2)} 0 0;
    vertical-align: middle;
    width: ${tombac.unit(4)};
  }
`;

const LimitValue = styled.strong`
  all: inherit;
  display: inline;
  font-weight: 500;
`;

const Title = styled.div`
  all: initial;
  display: block;
  ${tombac.altText({
    color: tombac.color("alert", 600),
    fontSize: 16,
    fontWeight: 500,
  })}
`;

const Container = styled.div<Pick<TrialLimitSummaryProps, "size">>`
  display: flex;

  ${tombac.variant<Pick<TrialLimitSummaryProps, "size">>("size", {
    m: css`
      & ${DaysRemaining} {
        height: ${tombac.unit(96)};
        width: ${tombac.unit(96)};
      }
      & ${DaysRemaining}::after {
        height: ${tombac.unit(48)};
      }
      & ${DaysRemainingCaption} {
        font-size: ${tombac.unit(10)};
      }
      & ${ExpiryDate} {
        color: ${tombac.color("neutral", 600)};
        font-size: ${tombac.unit(12)};
        margin-top: ${tombac.space(1)};
      }
      & ${LimitItem} {
        font-size: ${tombac.unit(12)};
      }
      & ${Title} {
        font-size: ${tombac.unit(18)};
      }
    `,
    l: css`
      & ${Content} {
        margin-left: ${tombac.space(6)};
      }
      & ${DaysRemaining} {
        height: ${tombac.unit(131)};
        position: relative;
        width: ${tombac.unit(149)};
      }
      & ${DaysRemaining}::after {
        height: ${tombac.unit(65)};
      }
      & ${DaysRemainingCaption} {
        font-size: ${tombac.unit(14)};
      }
      & ${DaysRemainingNumber} {
        font-size: ${tombac.unit(65)};
      }
      & ${ExpiryDate} {
        bottom: ${tombac.space(-4)};
        color: ${tombac.color("neutral", 700)};
        font-size: ${tombac.unit(12)};
        position: absolute;
        text-align: center;
        white-space: nowrap;
        width: 100%;
      }
      & ${ExpiryDate} > strong {
        color: ${tombac.color.black};
        font-weight: 500;
        position: static;
      }
      & ${LimitItem} {
        font-size: ${tombac.unit(14)};
      }
      & ${Title} {
        font-size: ${tombac.unit(24)};
      }
    `,
  })}
`;

export const TrialLimitsSummary: FC<TrialLimitSummaryProps> = ({
  activeProduct,
  onContinueClick,
  productAccess,
  size = "s",
}) => {
  const expiryDate = new Date(productAccess.expiryDate).getTime();
  const now = Date.now();
  const DAY_IN_MS = 24 * 60 * 60 * 1000;
  const diff = expiryDate - now;
  const days = Math.ceil(diff / DAY_IN_MS);

  const expiryDateElement = (
    <ExpiryDate>
      Expires on: <strong>{productAccess.expiryDate}</strong>
    </ExpiryDate>
  );

  let hasCustomDateRange = true;

  const dateRange = productAccess.limits.dateRange as {
    from: string;
    to: string;
  };

  if (dateRange && dateRange.from && dateRange.to) {
    const startDate = new Date(dateRange.from).getTime();
    const endDate = new Date(dateRange.to).getTime();

    if ((endDate - startDate) / DAY_IN_MS === 29) {
      hasCustomDateRange = false;
    }
  }

  return (
    <Container size={size}>
      <DaysRemaining>
        <DaysRemainingNumber>{days}</DaysRemainingNumber>{" "}
        <DaysRemainingCaption>days remaining</DaysRemainingCaption>
        {size === "l" && expiryDateElement}
      </DaysRemaining>
      <Content>
        <Title>Your free trial</Title>
        {size === "l" ? (
          <Text fontWeight={500} $marginTop="2sp">
            We hope you are happy with your free trial of TomTom{" "}
            {getProductName(activeProduct)}.
          </Text>
        ) : (
          expiryDateElement
        )}
        {activeProduct === "ts" || activeProduct === "od" ? (
          <LimitList>
            <LimitItem>
              <LimitValue>30 days</LimitValue> of trial access
            </LimitItem>
            <LimitItem>
              <LimitValue>
                <>{productAccess.limits.agreementJobs ?? 0} reports</>
              </LimitValue>{" "}
              to create
            </LimitItem>
            {!hasCustomDateRange && (
              <LimitItem>
                <LimitValue>1 month</LimitValue> of data
              </LimitItem>
            )}
          </LimitList>
        ) : (
          activeProduct === "ctm" && (
            <LimitList>
              <LimitItem>
                <LimitValue>30 days</LimitValue> of trial access
              </LimitItem>
              <LimitItem>
                <LimitValue>
                  <>{productAccess.limits.maxRouteCount ?? 0} routes</>
                </LimitValue>{" "}
                available
              </LimitItem>
            </LimitList>
          )
        )}
        {size === "l" && (
          <BrandButton $mt="4sp" onClick={onContinueClick}>
            Continue trial
          </BrandButton>
        )}
      </Content>
    </Container>
  );
};
